import { Headline, Layout, Text, TextButton } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { ConsumptionAndEmissionText } from '~/app/compositions/content/consumption-and-emission-text/consumption-emission-text';
import { VisualItem } from '~/app/compositions/content/gallery/items/gallery-items';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { Dialog } from '~/components/dialog';

import {
	StyledButtonContainer,
	StyledCarImage,
	StyledCarInfo,
	StyledDialog,
	StyledList,
} from '~/components/dialog/rescue-info-dialog/styles/rescue-info-dialog-styles';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';

export function RescueInfoDialog() {
	const { isDialogOpen } = useDialogs();

	return (
		<StyledDialog>
			<Dialog classNameDialog="custom-dialog" open={isDialogOpen('rescueInfo')} variant="alert">
				<RescueInfoDialogContent />
			</Dialog>
		</StyledDialog>
	);
}

function RescueInfoDialogContent() {
	const { closeDialog } = useDialogs();
	const { i18n } = useI18n();
	const { formatPriceWithVAT } = useCurrencyFormatter();
	const { mediaByProfileIdEnv1 } = useImages();
	const [{ adjustments, urlAudicode }] = useAtom(state.rescueInfoAdjustments);

	const { catalog } = useConfigurator();
	const carline = catalog.carInfo?.carline ?? 'Carline';
	const model = catalog.carInfo?.model ?? 'Model';
	const totalPrice = catalog.priceSummary?.totalPrice;

	const handleStartWithBaseConfiguration = () => {
		const newUrl = window.location.pathname;
		window.history.replaceState({ path: newUrl }, '', newUrl);
		window.location.reload();
	};

	const determineAdjustmentKey = (adjustment: RelevantRescueCodes) => {
		switch (adjustment) {
			case RelevantRescueCodes.BASE_MODEL_CHANGE:
			case RelevantRescueCodes.NEW_MODELYEAR:
				return i18n('ngc.rescueInfo.baseModelChange');
			case RelevantRescueCodes.TRIMLINE_CHANGED_EXTENSION:
			case RelevantRescueCodes.INTCOLOR_CHANGE:
			case RelevantRescueCodes.OPTION_DROPPED:
			case RelevantRescueCodes.CONFLICTS_SOLVED:
			case RelevantRescueCodes.OPTION_ADDED_DUE_TO_CONFLICT:
				return i18n('ngc.rescueInfo.equipmentChange');
			case RelevantRescueCodes.EXTCOLOR_CHANGED:
			case RelevantRescueCodes.EXCLUSIVE_COLOR_NOT_AVAILABLE:
				return i18n('ngc.rescueInfo.extColorChanged');
			case RelevantRescueCodes.OPTION_CHANGED_PRICE:
			case RelevantRescueCodes.EXTCOLOR_CHANGED_PRICE:
			case RelevantRescueCodes.AOZ_PRICE_CHANGED:
			case RelevantRescueCodes.MODEL_CHANGED_PRICE:
				return i18n('ngc.rescueInfo.priceChange');
			case RelevantRescueCodes.FINANCE_NOT_AVAILABLE:
			case RelevantRescueCodes.FINANCE_RATE_CHANGED:
				return i18n('ngc.rescueInfo.financingConditionsChanged');
			case RelevantRescueCodes.AOZ_CHANGED:
			case RelevantRescueCodes.AOZ_DROPPED:
				return i18n('ngc.rescueInfo.audiOriginalAccessories');
			default:
				return 'hidden';
		}
	};

	const memoizedDetermineAdjustmentKey = useMemo(
		() => adjustments.map(determineAdjustmentKey),
		[adjustments, determineAdjustmentKey],
	);

	return (
		<Layout direction="column">
			<Layout
				direction={{ sm: 'column', md: 'row' }}
				justify={{ sm: 'center', md: 'space-between' }}
				columnGap="40px">
				<StyledCarInfo>
					<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
						{i18n('ngc.rescueInfo.headline', { code: urlAudicode })}
					</Text>
					<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
						{carline}
					</Headline>
					<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>{model}</Text>
					<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{formatPriceWithVAT(totalPrice)}</Text>
				</StyledCarInfo>
				<StyledCarImage>
					<VisualItem item={mediaByProfileIdEnv1[ImageViewsEnum.EXTERIOR_FRONT_3_4]} size="small" />
				</StyledCarImage>
			</Layout>

			<Layout direction="column" rowGap="24px">
				<Layout direction="column">
					<Layout direction="column" rowGap="30px">
						<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
							{i18n('ngc.rescueInfo.audiCodeNotAvailable')}
						</Text>

						<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
							{i18n('ngc.rescueInfo.alternativeConfiguration')}
						</Text>
					</Layout>

					<StyledList>
						{[...new Set(memoizedDetermineAdjustmentKey)].map(
							(adjustment) =>
								adjustment !== 'hidden' && (
									<li key={adjustment}>
										<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
											{adjustment}
										</Text>
									</li>
								),
						)}
					</StyledList>

					<StyledButtonContainer>
						<TextButton variant="primary" size="sm" isFullWidth onClick={() => closeDialog('rescueInfo')}>
							{i18n('ngc.rescueInfo.loadUpdatedConfiguration')}
						</TextButton>
						<TextButton
							variant="secondary"
							size="sm"
							isFullWidth
							onClick={handleStartWithBaseConfiguration}>
							{i18n('ngc.rescueInfo.startWithBaseConfiguration')}
						</TextButton>
					</StyledButtonContainer>

					<ConsumptionAndEmissionText />
				</Layout>
			</Layout>
		</Layout>
	);
}

export enum RelevantRescueCodes {
	BASE_MODEL_CHANGE = 'BASE_MODEL_CHANGE',
	NEW_MODELYEAR = 'NEW_MODELYEAR',
	TRIMLINE_CHANGED_EXTENSION = 'TRIMLINE_CHANGED_EXTENSION',
	INTCOLOR_CHANGE = 'INTCOLOR_CHANGE',
	OPTION_DROPPED = 'OPTION_DROPPED',
	CONFLICTS_SOLVED = 'CONFLICTS_SOLVED',
	OPTION_ADDED_DUE_TO_CONFLICT = 'OPTION_ADDED_DUE_TO_CONFLICT',
	EXTCOLOR_CHANGED = 'EXTCOLOR_CHANGED',
	EXCLUSIVE_COLOR_NOT_AVAILABLE = 'EXCLUSIVE_COLOR_NOT_AVAILABLE',
	OPTION_CHANGED_PRICE = 'OPTION_CHANGED_PRICE',
	EXTCOLOR_CHANGED_PRICE = 'EXTCOLOR_CHANGED_PRICE',
	AOZ_PRICE_CHANGED = 'AOZ_PRICE_CHANGED',
	MODEL_CHANGED_PRICE = 'MODEL_CHANGED_PRICE',
	FINANCE_NOT_AVAILABLE = 'FINANCE_NOT_AVAILABLE',
	FINANCE_RATE_CHANGED = 'FINANCE_RATE_CHANGED',
	AOZ_CHANGED = 'AOZ_CHANGED',
	AOZ_DROPPED = 'AOZ_DROPPED',
}
